﻿import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Box, Typography, IconButton } from "@mui/material";
import {
  updateFields,
  resetFields,
  selectSelectedSchema,
  setArtifactHeaderInfo,
  setArtifactDetailInfo,
  setArtifactHeaderOptionalFieldInfo,
  setArtifactDetailOptionalFieldInfo,
  setArtifactHeaderConsolidatedInfo,
  setArtifactDetailConsolidatedInfo,
  setIsMappingClicked,
} from "@/features/mapSchema/mapSchemaSlice";
import {
  ShepDataGridPro,
  ShepCheckbox,
  ShepPagination,
  ShepHorizontalMenu,
  ShepGridWarning,
  ShepTooltip,
} from "@/components/GenericComponents/ShepComponents";
import AddEditInboundMapping from "./AddEditInboundMapping";
import { v4 as uuidv4, type UUIDTypes } from "uuid";
import axios from "@/features/axiosHelper";
import { createAlert } from "@/features/homepage/alertSliceList";
import EditFillIcon from "@/assets/img/icons/Edit/Fill.svg?react";
import { FooterButton } from "@/components/Integrations/ManageConnection/ManageConnectionPopupContext";
import type { IArtifactInboundMapping, IMapSchemaField, IOptionalFieldDto } from "@/features/sharedTypes";
import type { GridRenderCellParams } from "@mui/x-data-grid";

interface IArtifactInboundMappingDto{
  Id: UUIDTypes;
  Script: string;
  ShowInReport: boolean;
  IsOptionalField: boolean;
  IsDisabled: boolean;
  Description: string;
  Source: IArtifactMappingDocumentLocationDto;
  Target: IArtifactMappingArtifactLocationDto;
}
interface IArtifactMappingArtifactLocationDto {
  ArtifactType: number | null;
  FieldName: string;
}
interface IArtifactMappingDocumentLocationDto {
  MappingArea: number | null;
  MappingSegmentField: string;
  MappingValuePosition: number | null;
  ConditionSegment: string;
  Occurrence: number | null;
  Prefix: string;
  Suffix: string;
}

const EdiInboundMap = () => {
  const dispatch = useDispatch();

  const [isAdding, setIsAdding] = useState<boolean>(false); //to store the state of the Add button click
  const [isEditing, setIsEditing] = useState<boolean>(false); //to store the state of the Edit button click
  const [inboundMapHeaderData, setInboundMapHeaderData] = useState<IArtifactInboundMapping[]>([]);
  const [inboundMapDetailData, setInboundMapDetailData] = useState<IArtifactInboundMapping[]>([]);
  const [selectedRows, setSelectedRows] = useState<IArtifactInboundMapping[]>([]);
  const [matchingRecordToEdit, setMatchingRecordToEdit] = useState<IArtifactInboundMapping[]>([]);

  // Inline row edit handler used by the edit icon inside the grid
  function inlineEdit(row: IArtifactInboundMapping) {
    if (!row) return;
    // Populate redux form fields from the row
    dispatch(resetFields());
    dispatch(updateFields({ key: "id", value: row.guid }));
    dispatch(
      updateFields({ key: "mappingSegment", value: row.MappingSegmentField }),
    );
    dispatch(
      updateFields({
        key: "valuePosition",
        value:
          row.MappingValuePosition != null
            ? String(row.MappingValuePosition)
            : "",
      }),
    );
    dispatch(
      updateFields({ key: "conditionSegment", value: row.ConditionSegment }),
    );
    dispatch(
      updateFields({
        key: "occurrence",
        value: row.Occurrence != null ? String(row.Occurrence) : "",
      }),
    );
    dispatch(updateFields({ key: "prefix", value: row.Prefix }));
    dispatch(updateFields({ key: "suffix", value: row.Suffix }));
    dispatch(updateFields({ key: "showInReport", value: row.ShowInReport }));
    dispatch(updateFields({ key: "mappingArea", value: row.MappingArea }));
    dispatch(updateFields({ key: "targetField", value: row.FieldName }));
    dispatch(
      updateFields({
        key: "targetFieldIsOptional",
        value: row.IsOptionalField,
      }),
    );
    if (row.Script !== undefined) {
      dispatch(updateFields({ key: "script", value: row.Script }));
    }
    setMatchingRecordToEdit([row]);
    setIsAdding(false);
    setIsEditing(true);
    setSelectedRows([]);
    // Clear selection models (they’ll be reset when returning)
    setHeaderRowSelectionModel([]);
    setDetailRowSelectionModel([]);
  }

  const columns = [
    {
      field: "MappingSegmentField",
      headerName: "Source Attribute",
      width: 220,
    },
    {
      field: "MappingValuePosition",
      headerName: "Value Position",
      width: 200,
    },
    {
      field: "ConditionSegment",
      headerName: "Condition Segment",
      width: 240,
    },
    {
      field: "FieldName",
      headerName: "Target Attribute",
      flex: 1,
      minWidth: 170,
    },
    {
      field: "Script",
      headerName: "JS Script",
      width: 130,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <ShepCheckbox
        //@ts-expect-error checked property is not defined on ShepCheckbox (passthrough prop)
            checked={!!(params.row.Script && params.row.Script.trim() !== "")}
            disabled
        />
      ),
    },
    {
      field: "Prefix",
      headerName: "Prefix",
      width: 150,
    },
    {
      field: "Suffix",
      headerName: "Suffix",
      width: 150,
    },
    {
      field: "editAction",
      headerName: "",
      width: 60,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <ShepTooltip title="Edit Mapping">
          <IconButton
            className="workflow-row"
            size="small"
            disableRipple
            onClick={(e) => {
              e.stopPropagation();
              inlineEdit(params.row);
            }}
          >
            <EditFillIcon />
          </IconButton>
        </ShepTooltip>
      ),
    },
  ];

  //header
  const [artifactHeaderData, setArtifactHeaderData] = useState([]);
  const [artifactHeaderOptionalField, setArtifactHeaderOptionalField] =
    useState([]);
  const [artifactHeaderConsolidated, setArtifactHeaderConsolidated] = useState(
    [],
  );

  //detail
  const [artifactDetailData, setArtifactDetailData] = useState([]);
  const [artifactDetailOptionalField, setArtifactDetailOptionalField] =
    useState([]);
  const [artifactDetailConsolidated, setArtifactDetailConsolidated] = useState(
    [],
  );

  //header
  const onClickAddHeader = () => {
    dispatch(resetFields());
    dispatch(
      updateFields({
        key: "id",
        value: uuidv4(),
      }),
    );
    dispatch(updateFields({ key: "mappingArea", value: 0 }));
    setIsAdding(true);
    setIsEditing(false);
    setSelectedRows([]);
  };

  //detail
  const onClickAddDetail = () => {
    dispatch(resetFields());
    dispatch(
      updateFields({
        key: "id",
        value: uuidv4(),
      }),
    );
    dispatch(updateFields({ key: "mappingArea", value: 1 }));
    setIsAdding(true);
    setIsEditing(false);
    setSelectedRows([]);
  };

  const onClickEditDetail = () => {
    if (selectedRows.length !== 0) {
      const row = selectedRows[0];
      inlineEdit(row);
    } else {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          message: "No record selected",
          buttonLabel: "Close",
        }),
      );
    }
  };

  const validateDelete = () => {
    dispatch(
      createAlert({
        status: "Warning",
        alertType: "result",
        message: "Are you sure you want to delete the record?",
        buttonLabel: "Delete",
        onButtonClick: () => deleteInboundData(),
        buttonLabel2: "Cancel",
      }),
    );
  };

  //delete for header and detail
  const deleteInboundData = () => {
    if (selectedRows.length !== 0) {
      const selectedRow = selectedRows[0]
      axios
        .delete(`/mapschema/inbound/delete`, {
          params: {
            ids: selectedRow.guid,
            connectionArtifactSchemaId:
              selectedSchema.ConnectionArtifactSchemaId,
          },
        })
        .then((response) => {
          if (response.data) {
            dispatch(
              createAlert({
                status: "Success",
                alertType: "result",
                message: "The record was deleted successfully.",
                buttonLabel: "Close",
              }),
            );
            getInboundMapData();

            // heap.io
            window.heap.track("Inbound Mapping - Delete Map");
          } else {
            dispatch(
              createAlert({
                status: "Error",
                alertType: "result",
                message: "There was an error while deleting the record.",
                buttonLabel: "Close",
              }),
            );
          }
        })
        .catch((error) => console.error(error))
        .finally(() => {
          setHeaderRowSelectionModel([]);
          setDetailRowSelectionModel([]);
          setSelectedRows([]);
        });
    } else {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          message: "No record selected to delete",
          buttonLabel: "Close",
        }),
      );
    }
  };

  const selectedSchema = useSelector(selectSelectedSchema); //selected document from mapping screen

  //The 'getInboundMapData' function makes the dependencies of useEffect Hook (at line 363) change on every render. To fix this, wrap the definition of 'getInboundMapData' in its own useCallback() Hook
  const getInboundMapData = useCallback(() => {
    axios
      .get("/mapschema/inbound/inbound-map-data", {
        params: {
          connectionArtifactSchemaId: selectedSchema.ConnectionArtifactSchemaId,
        },
      })
      .then((response) => {
        if (response.data.length === 0) {
          setInboundMapHeaderData([]);
          setInboundMapDetailData([]);
          return;
        } else {
          const result = response.data;
          const inboundMapData = result.map((x: IArtifactInboundMappingDto, i: number): IArtifactInboundMapping => {
            return {
              id: String(i),
              guid: x.Id,
              IsDisabled: !x.IsDisabled,
              ShowInReport: x.ShowInReport,
              IsOptionalField: x.IsOptionalField,
              Script: x.Script,
              ...x.Source,
              ...x.Target,
            };
          });
          setInboundMapHeaderData(
            inboundMapData.filter((x: IArtifactInboundMapping) => x.MappingArea === 0),
          );
          setInboundMapDetailData(
            inboundMapData.filter((x: IArtifactInboundMapping) => x.MappingArea === 1),
          );
        }
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
      });
  }, [selectedSchema.ConnectionArtifactSchemaId]);

  const getArtifactProps = (artifactType: number) => {
    axios
      .get("/mapschema/inbound/artifact-props", {
        params: {
          artifactType: artifactType,
        },
      })
      .then((response) => {
        if (response.data !== "" && response.data !== undefined) {
          const documentHeader = response.data.DocumentHeader;
          const documentDetails = response.data.DocumentDetails;

          if (documentHeader !== undefined) {
            setArtifactHeaderData(documentHeader);
          } else {
            setArtifactHeaderData([]);
          }

          if (documentDetails !== undefined) {
            setArtifactDetailData(documentDetails);
          } else {
            setArtifactDetailData([]);
          }
        } else {
          //clean up later
          setArtifactHeaderData([]);
          setArtifactDetailData([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
      });
  };

  const getArtifactOptionalFields = (artifactType: number) => {
    axios
      .get("/mapschema/artifact-optional-field", {
        params: { artifactType: artifactType, detailLevel: 0 },
      })
      .then((response) => {
        const data = response.data.map((x: IOptionalFieldDto) : IMapSchemaField => {
          const detail = JSON.parse(x.JSONDetails);
          return {
            id: x.ArtifactOptionalFieldId,
            field: detail.Path,
            type: "String",
            maxLength: 0,
            isOptional: true,
          };
        });
        setArtifactHeaderOptionalField(data);
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
      });

    axios
      .get("/mapschema/artifact-optional-field", {
        params: { artifactType: artifactType, detailLevel: 1 },
      })
      .then((response) => {
        const data = response.data.map((x: IOptionalFieldDto) : IMapSchemaField => {
          const detail = JSON.parse(x.JSONDetails);
          return {
            id: x.ArtifactOptionalFieldId,
            field: detail.Path,
            type: "String",
            maxLength: 0,
            isOptional: true,
          };
        });
        setArtifactDetailOptionalField(data);
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
      });
  };

  // moving these, getArtifactProps and getArtifactOptionalFields must be declared before being used, moving others for consistency
  useEffect(() => {
    getArtifactProps(selectedSchema.ArtifactType);
    getArtifactOptionalFields(selectedSchema.ArtifactType);
  }, [selectedSchema]);

  useEffect(() => {
    getInboundMapData();
  }, [getInboundMapData]);

  useEffect(() => {
    getInboundMapData();
  }, [isAdding, isEditing, getInboundMapData]);

  useEffect(() => {
    setArtifactHeaderConsolidated(
      artifactHeaderData.concat(artifactHeaderOptionalField),
    );
  }, [artifactHeaderData, artifactHeaderOptionalField]);
  
  useEffect(() => {
    setArtifactDetailConsolidated(
      artifactDetailData.concat(artifactDetailOptionalField),
    );
  }, [artifactDetailData, artifactDetailOptionalField]);

  //storing artifact header and detail info in redux
  dispatch(setArtifactHeaderInfo({ artifactHeaderData: artifactHeaderData }));
  dispatch(setArtifactDetailInfo({ artifactDetailData: artifactDetailData }));

  dispatch(
    setArtifactHeaderOptionalFieldInfo({
      artifactHeaderOptionalField: artifactHeaderOptionalField,
    }),
  );
  dispatch(
    setArtifactDetailOptionalFieldInfo({
      artifactDetailOptionalField: artifactDetailOptionalField,
    }),
  );

  dispatch(
    setArtifactHeaderConsolidatedInfo({
      artifactHeaderConsolidated: artifactHeaderConsolidated,
    }),
  );
  dispatch(
    setArtifactDetailConsolidatedInfo({
      artifactDetailConsolidated: artifactDetailConsolidated,
    }),
  );

  //header
  const [headerRowSelectionModel, setHeaderRowSelectionModel] = useState<string[]>([]);

  const handleHeaderRowSelection = (newRowSelectionModel: string[]) => {
    if (newRowSelectionModel.length > 1) {
      newRowSelectionModel = [newRowSelectionModel.pop() as string];
      setHeaderRowSelectionModel(newRowSelectionModel);
    } else {
      setHeaderRowSelectionModel(newRowSelectionModel);
    }

    const selectedData = inboundMapHeaderData.find(
      (row) => row.id === newRowSelectionModel[0],
    );
    if (selectedData) {
      setSelectedRows([selectedData]);
    } else {
      setSelectedRows([]);
    }
  };

  //detail
  const [detailRowSelectionModel, setDetailRowSelectionModel] = useState<string[]>([]);
  const handleDetailRowSelection = (newRowSelectionModel: string[]) => {
    if (newRowSelectionModel.length > 1) {
      newRowSelectionModel = [newRowSelectionModel.pop() as string];
      setDetailRowSelectionModel(newRowSelectionModel);
    } else {
      setDetailRowSelectionModel(newRowSelectionModel);
    }

    const selectedData = inboundMapDetailData.find(
      (row) => row.id === newRowSelectionModel[0],
    );
    if (selectedData) {
      setSelectedRows([selectedData]);
    } else {
      setSelectedRows([]);
    }
  };

  return (
    <Container
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
        minHeight: 0,
        padding: "0 0 0 20px",
      }}
    >
      {/* Footer button rendered via portal to FooterBarHost */}
      <FooterButton
        variant="secondary"
        onClick={() =>
          dispatch(setIsMappingClicked({ isMappingClicked: false }))
        }
        hidden={isAdding || isEditing}
      >
        Back
      </FooterButton>
      {isAdding || isEditing ? (
        <AddEditInboundMapping
          onBack={() => {
            setIsAdding(false);
            setIsEditing(false);
          }}
          isEditMode={isEditing}
          recordToEdit={matchingRecordToEdit[0]}
        />
      ) : (
        <>
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                padding: "10px",
              }}
            >
              {selectedSchema?.DocumentDescription && (
                //@ts-expect-error variant outline does not exist, not sure what is expected here?
                <Typography variant="outline" sx={{ fontSize: 20 }}>
                  {selectedSchema.DocumentDescription}
                </Typography>
              )}
              <ShepHorizontalMenu
                actions={[
                  { label: "Add", onClick: onClickAddHeader },
                  {
                    label: "Delete",
                    onClick: validateDelete,
                    variant: "danger",
                  },
                ]}
              />
            </Box>
            <Box
              className="content-box"
              sx={{ display: "flex", flexDirection: "row" }}
            >
              <Box sx={{ width: "100%" }}>
                <Box
                  sx={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}
                >
                  <Box
                    sx={{
                      height: "78px",
                      backgroundColor: "var(--primary-main)",
                      color: "var(--default-light)",
                      padding: "20px 0px 20px 53px",
                      borderRadius: "4px",
                      border: "1px black",
                      boxSizing: "border-box",
                    }}
                  >
                    <Typography
                      style={{
                        fontSize: "var(--font-lg)",
                        fontWeight: "var(--font-semi-bold)",
                      }}
                    >
                      Header
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {inboundMapHeaderData.length > 0 ? (
              <ShepDataGridPro
                isDataLoaded={true}
                rows={inboundMapHeaderData}
                columns={columns}
                getRowId={(row: IArtifactInboundMapping) => row.id}
                columnHeaderHeight={78}
                rowHeight={100}
                pagination
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 12, page: 0 },
                  },
                }}
                whiteHeader={true}
                checkboxSelection={true}
                disableMultipleRowSelection={true}
                disableColumnMenu={true}
                disableColumnFilter={true}
                disableColumnReorder={true}
                hideFooterSelectedRowCount={true}
                columnVisibilityModel={{
                  endpointSchemaId: false,
                }}
                onRowSelectionModelChange={handleHeaderRowSelection}
                rowSelectionModel={headerRowSelectionModel}
                disableRowSelectionOnClick={true}
                slots={{
                  baseCheckbox: ShepCheckbox,
                  pagination: ShepPagination,
                }}
                sx={{
                  color: "var(--text-primary)",
                  fontSize: "var(--font-sm)",
                  fontFamily: "var(--font-family)",
                  borderTopLeftRadius: "44px",
                  borderTopRightRadius: "44px",
                  backgroundColor: "var(--default-light)",
                  border: "none",
                  "& .MuiDataGrid:hover": {
                    backgroundColor: "inherit",
                  },
                  "& .MuiDataGrid-cellCheckbox, & .workflow-row": {
                    opacity: 0,
                    borderBottom: "none",
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: "none",
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "var(--tertiary-13)",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "var(--primary-pale)",
                  },
                  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus":
                    {
                      outline: "none",
                    },
                  "& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox, & .MuiDataGrid-row:hover .workflow-row":
                    {
                      opacity: 1,
                    },
                  "& .Mui-selected .MuiDataGrid-cellCheckbox": {
                    opacity: 1,
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontSize: "var(--font-md)",
                    fontWeight: "var(--font-bold)",
                    borderLeft: "none",
                  },
                  "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover":
                    {
                      borderBottomLeftRadius: "4px",
                      borderBottomRightRadius: "4px",
                      backgroundColor: "var(--primary-pale)",
                      borderBottom: "none",
                      borderLeft: "none",
                    },
                  "& .MuiDataGrid-columnHeader[data-field='displayName']": {
                    marginLeft: 0,
                  },
                  "& .MuiDataGrid-cell[data-field='displayName']": {
                    marginLeft: 0,
                  },
                }}
              />
            ) : (
              //@ts-expect-error sx is not defined on ShepGridWarning
              <ShepGridWarning sx={{ marginTop: "25px" }} />
            )}
          </Box>

          {/* Detail section */}
          <Box
            style={{
              marginTop: "30px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "10px",
              }}
            >
              <ShepHorizontalMenu
                actions={[
                  { label: "Add", onClick: onClickAddDetail },
                  { label: "Edit", onClick: onClickEditDetail },
                  {
                    label: "Delete",
                    onClick: validateDelete,
                    variant: "danger",
                  }
                ]}
              />
            </Box>
            <Box
              className="content-box"
              sx={{ display: "flex", flexDirection: "row" }}
            >
              <Box sx={{ width: "100%" }}>
                <Box
                  sx={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}
                >
                  <Box
                    sx={{
                      height: "78px",
                      backgroundColor: "var(--primary-main)",
                      color: "var(--default-light)",
                      padding: "20px 0px 20px 53px",
                      borderRadius: "4px",
                      border: "1px black",
                      boxSizing: "border-box",
                    }}
                  >
                    <Typography
                      style={{
                        fontSize: "var(--font-lg)",
                        fontWeight: "var(--font-semi-bold)",
                      }}
                    >
                      Detail
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {inboundMapDetailData.length > 0 ? (
              <ShepDataGridPro
                isDataLoaded={true}
                rows={inboundMapDetailData}
                columns={columns}
                getRowId={(row: IArtifactInboundMapping) => row.id}
                columnHeaderHeight={78}
                rowHeight={100}
                pagination
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 12, page: 0 },
                  },
                }}
                whiteHeader={true}
                checkboxSelection={true}
                disableMultipleRowSelection={false}
                disableColumnMenu={true}
                disableColumnFilter={true}
                disableColumnReorder={true}
                hideFooterSelectedRowCount={true}
                columnVisibilityModel={{
                  endpointSchemaId: false,
                }}
                onRowSelectionModelChange={handleDetailRowSelection}
                rowSelectionModel={detailRowSelectionModel}
                disableRowSelectionOnClick={true}
                slots={{
                  baseCheckbox: ShepCheckbox,
                  pagination: ShepPagination,
                }}
                sx={{
                  color: "var(--text-primary)",
                  fontSize: "var(--font-sm)",
                  fontFamily: "var(--font-family)",
                  borderTopLeftRadius: "44px",
                  borderTopRightRadius: "44px",
                  backgroundColor: "var(--default-light)",
                  border: "none",
                  "& .MuiDataGrid:hover": {
                    backgroundColor: "inherit",
                  },
                  "& .MuiDataGrid-cellCheckbox, & .workflow-row": {
                    opacity: 0,
                    borderBottom: "none",
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: "none",
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "var(--tertiary-13)",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "var(--primary-pale)",
                  },
                  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus":
                    {
                      outline: "none",
                    },
                  "& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox, & .MuiDataGrid-row:hover .workflow-row":
                    {
                      opacity: 1,
                    },
                  "& .Mui-selected .MuiDataGrid-cellCheckbox": {
                    opacity: 1,
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontSize: "var(--font-md)",
                    fontWeight: "var(--font-bold)",
                    borderLeft: "none",
                  },
                  "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover":
                    {
                      borderBottomLeftRadius: "4px",
                      borderBottomRightRadius: "4px",
                      backgroundColor: "var(--primary-pale)",
                      borderBottom: "none",
                      borderLeft: "none",
                    },
                  "& .MuiDataGrid-columnHeader[data-field='displayName']": {
                    marginLeft: 0,
                  },
                  "& .MuiDataGrid-cell[data-field='displayName']": {
                    marginLeft: 0,
                  },
                }}
              />
            ) : (
              //@ts-expect-error sx is not defined on ShepGridWarning
              <ShepGridWarning sx={{ marginTop: "25px" }} />
            )}
          </Box>
        </>
      )}
    </Container>
  );
};
export default EdiInboundMap;
